import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/helpers";
import { fetchEquipmentImagesForIds } from "@/utils/multipleImageHandling";
import { deduplicateImageUrls } from "@/utils/imageDeduplication";
import { syncShopGearFromEndpoint } from "@/services/equipment/shopGearSyncService";
import { normalizeCurrencyCode } from "@/utils/currency";
import { isMissingCurrencyCodeColumnError } from "@/utils/supabaseCurrencyCompat";

interface UserEquipment {
  id: string;
  user_id: string;
  name: string;
  category: string;
  description: string;
  price_per_day: number;
  price_per_hour?: number;
  price_per_week?: number;
  currency_code?: string;
  damage_deposit?: number;
  images: string[]; // Images array from equipment_images table
  rating: number;
  review_count: number;
  status: "available" | "booked" | "unavailable";
  created_at: string;
  updated_at: string;
  visible_on_map: boolean;
  location: {
    lat: number;
    lng: number;
    address: string; // Changed from zip to address
  };
  specifications: {
    size: string;
    weight: string;
    material: string;
    suitable: string;
  };
  availability: {
    available: boolean;
  };
}

export const useUserEquipment = (
  userId?: string,
  visibleOnly: boolean = false,
) => {
  const { user } = useAuth();
  const effectiveUserId = userId || user?.id;

  const buildEquipmentQuery = (includeCurrencyCode: boolean) => {
    const columns = "id, user_id, name, category, description, price_per_day, price_per_hour, price_per_week, damage_deposit, rating, review_count, status, created_at, updated_at, visible_on_map, location_lat, location_lng, location_address, size, weight, material, suitable_skill_level" as const;
    const equipment = supabase.from("equipment");
    // Keep each projection literal so the database client can infer its row type.
    let query = (includeCurrencyCode
      ? equipment.select(`${columns}, currency_code`)
      : equipment.select(columns))
      .eq("user_id", effectiveUserId);

    if (visibleOnly) {
      query = query.eq("visible_on_map", true);
    }

    return query.order("created_at", {
      ascending: false,
    });
  };

  return useQuery({
    queryKey: ["userEquipment", effectiveUserId, visibleOnly],
    queryFn: async (): Promise<UserEquipment[]> => {
      if (!effectiveUserId) {
        throw new Error("User ID is required");
      }

      let { data, error } = await buildEquipmentQuery(true);

      if (isMissingCurrencyCodeColumnError(error)) {
        ({ data, error } = await buildEquipmentQuery(false));
      }

      if (error) {
        console.error("Error fetching user equipment:", error);
        throw error;
      }

      // Batch-fetch images for all equipment items in a single query
      const imagesByEquipmentId = await fetchEquipmentImagesForIds(
        (data || []).map((item) => item.id),
      );

      const equipmentWithImages = (data || []).map((item) => {
          // Use images from equipment_images table or fallback to primary
          const allImages = deduplicateImageUrls(
            imagesByEquipmentId.get(item.id) || [],
          );

          return {
            id: item.id,
            user_id: item.user_id,
            name: item.name,
            category: item.category,
            description: item.description || "",
            price_per_day: item.price_per_day,
            price_per_hour: item.price_per_hour,
            price_per_week: item.price_per_week,
            currency_code: normalizeCurrencyCode(
              "currency_code" in item && typeof item.currency_code === "string"
                ? item.currency_code
                : undefined,
            ),
            damage_deposit: item.damage_deposit,
            images: allImages, // Include all images
            rating: item.rating || 0,
            review_count: item.review_count || 0,
            status: item.status as "available" | "booked" | "unavailable",
            created_at: item.created_at,
            updated_at: item.updated_at,
            visible_on_map: item.visible_on_map,
            location: {
              lat: item.location_lat || 0,
              lng: item.location_lng || 0,
              address: item.location_address || "", // Changed from zip to address
            },
            specifications: {
              size: item.size || "",
              weight: item.weight || "",
              material: item.material || "",
              suitable: item.suitable_skill_level || "",
            },
            availability: {
              available: item.status === "available",
            },
            owner: {
              id: item.user_id,
              name: "User", // We don't have profile name in this query
            },
          };
        });

      return equipmentWithImages;
    },
    enabled: !!effectiveUserId,
  });
};

export const useDeleteEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (equipmentId: string) => {
      const { data, error } = await supabase
        .from("equipment")
        .delete()
        .eq("id", equipmentId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error("Equipment not found or you don't have permission to delete it");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userEquipment"] });
    },
  });
};

export const useUpdateEquipmentVisibility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      equipmentId,
      visible,
    }: {
      equipmentId: string;
      visible: boolean;
    }) => {
      const { error } = await supabase
        .from("equipment")
        .update({ visible_on_map: visible })
        .eq("id", equipmentId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userEquipment"] });
    },
  });
};

export const useSyncShopGearEndpoint = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (endpointInput: string) => {
      if (!user?.id) {
        throw new Error("You must be signed in to sync shop gear.");
      }

      return await syncShopGearFromEndpoint({
        userId: user.id,
        endpointInput,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userEquipment"] });
      queryClient.invalidateQueries({ queryKey: ["equipment"] });
    },
  });
};
